import { CursorClickIcon } from '@phosphor-icons/react';

import { CloseButton } from '@/components/common/close-button';

import styles from './mapping-banner.module.scss';

/** 113600, Field Mapping Screen Elements — the wording is the criterion's, verbatim. */
export const MAPPING_BANNER_TEXT = 'Click or enter coordinates to mark field';

/** 113609, Placement mode — the acceptance criterion's wording, verbatim (Figma `1657:789739`). */
export const PLACEMENT_BANNER_TEXT = 'Drag to move the shape';

export type MappingBannerProps = {
  /** The Grower pressed the ×. **The page owns the dismissed flag**, not this component: that is
   *  what makes "stays dismissed for the remainder of the mapping session, including across Shape
   *  mode switches" true by construction — the flag does not live anywhere a tool change reaches. */
  onDismiss: () => void;
  /**
   * What the banner says. Defaults to Field Mapping mode's wording; **Placement mode** passes
   * {@link PLACEMENT_BANNER_TEXT}.
   *
   * One component for both because Figma `1657:789735` and `587:523530` are the same pill down to
   * the padding — but they remain **independent banners**, because the dismissed flag has always
   * lived with the page and not here. That is what makes "dismissing this one has no effect on the
   * one shown later in Field Mapping mode" true by construction: there is no shared state to leak
   * through.
   */
  text?: string;
  /**
   * An extra class on the pill, for a page that needs to place it somewhere other than the shared
   * `top`. The Remap Device page is the one such caller: Figma bottom-aligns its pill with the
   * search pill beside it (both ending at y=139), where Field Mapping has no search pill and keeps
   * the 99px this component defaults to. Composed rather than replaced, so a caller overrides the
   * offset and inherits every other rule.
   */
  className?: string;
};

/**
 * The helper banner across the canvas top (Figma `587:523530`).
 *
 * Rendered only while undismissed — the page simply stops rendering it — so there is no hidden
 * element for a screen reader to find and no `display: none` state to style.
 */
export function MappingBanner({
  text = MAPPING_BANNER_TEXT,
  onDismiss,
  className,
}: MappingBannerProps) {
  return (
    <div className={className ? `${styles.banner} ${className}` : styles.banner}>
      <p className={styles.message}>
        <CursorClickIcon size={24} aria-hidden />
        {text}
      </p>
      <CloseButton aria-label="Dismiss" className={styles.dismiss} onClick={onDismiss} />
    </div>
  );
}
